import { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2, ArrowRight, Sparkles } from "lucide-react";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { useBilling } from "@/context/BillingContext";
import { toast } from "sonner";

export default function BillingSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling");
  const { refresh } = useBilling();

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let cancelled = false;
    let tries = 0;
    const poll = async () => {
      tries += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setStatus("paid");
          await refresh();
          toast.success("Welcome to Pro! 🚀");
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          setStatus("failed");
          return;
        }
      } catch {
        /* ignore transient errors */
      }
      if (tries < 15) {
        setTimeout(poll, 2000);
      } else {
        setStatus("timeout");
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <main className="max-w-xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center">
          {status === "polling" && (
            <div data-testid="billing-polling">
              <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto" />
              <h1 className="text-2xl font-bold text-slate-900 mt-6">Confirming your payment…</h1>
              <p className="text-slate-500 mt-2">This usually takes a couple of seconds.</p>
            </div>
          )}
          {status === "paid" && (
            <div data-testid="billing-paid">
              <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-8 h-8 text-emerald-600" />
              </div>
              <h1 className="text-3xl font-black tracking-tight text-slate-900 mt-6">
                You're on <span className="text-blue-600">Pro</span> ✨
              </h1>
              <p className="text-slate-600 mt-3 max-w-sm mx-auto">
                Unlimited campaigns, CSV export, custom domain, and scan alerts are all unlocked.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  onClick={() => navigate("/dashboard")}
                  className="rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold"
                  data-testid="billing-back-to-dashboard"
                >
                  Back to dashboard <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
                <Link to="/settings">
                  <Button variant="outline" className="rounded-xl">
                    <Sparkles className="w-4 h-4 mr-1" />
                    Set up custom domain
                  </Button>
                </Link>
              </div>
            </div>
          )}
          {(status === "failed" || status === "error" || status === "timeout") && (
            <div data-testid="billing-error">
              <h1 className="text-2xl font-bold text-slate-900">Something went wrong</h1>
              <p className="text-slate-500 mt-2">
                {status === "timeout"
                  ? "We couldn't confirm your payment in time. Refresh in a minute or contact support."
                  : "Your payment didn't go through. Feel free to try again."}
              </p>
              <div className="mt-6">
                <Link to="/pricing">
                  <Button variant="outline" className="rounded-xl">Back to pricing</Button>
                </Link>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
